import { endOfDay, isValid, startOfDay } from 'date-fns';

export function parseClosingDate(dateStr) {
    if (!dateStr) return null;
    const normalized = String(dateStr).includes('T') ? dateStr : String(dateStr).replace(' ', 'T');
    const date = new Date(normalized);
    return isValid(date) ? date : null;
}

export function getClosingPeriodsForDate(closingPeriods, date) {
    if (!closingPeriods?.length || !date) return [];
    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);
    return closingPeriods.filter((p) => {
        const start = parseClosingDate(p?.start_date);
        const end = parseClosingDate(p?.end_date);
        if (!start || !end) return false;
        return start <= dayEnd && end >= dayStart;
    });
}

/** Block calendar day only when a closing period covers the full day (not half-days). */
export function isDayFullyClosed(date, closingPeriods) {
    if (!closingPeriods?.length || !date) return false;
    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);
    return closingPeriods.some((p) => {
        const periodStart = parseClosingDate(p?.start_date);
        const periodEnd = parseClosingDate(p?.end_date);
        if (!periodStart || !periodEnd) return false;
        return periodStart <= dayStart && periodEnd >= dayEnd;
    });
}

/**
 * @param {{ inclusiveEnd?: boolean }} options
 * - inclusiveEnd false (default): end_date is the first open slot — e.g. closed until 13:30 → 13:30 is bookable
 * - inclusiveEnd true: end_date is still closed — used to detect fully closed availability slots
 */
/** Full datetime (search pickup/return) — exclusive end by default: reopening at end_date is bookable. */
export function isMomentInClosingPeriod(date, closingPeriods, { inclusiveEnd = false } = {}) {
    if (!closingPeriods?.length || date == null) return false;
    const moment = date instanceof Date ? date : parseClosingDate(date);
    if (!moment || !isValid(moment)) return false;
    const t = moment.getTime();
    return getClosingPeriodsForDate(closingPeriods, moment).some((p) => {
        const start = parseClosingDate(p.start_date);
        const end = parseClosingDate(p.end_date);
        if (!start || !end) return false;
        if (t < start.getTime()) return false;
        return inclusiveEnd ? t <= end.getTime() : t < end.getTime();
    });
}

export function isTimeInClosingPeriod(date, hour, minute, closingPeriods, { inclusiveEnd = false } = {}) {
    const periods = getClosingPeriodsForDate(closingPeriods, date);
    if (!periods.length || date == null) return false;
    const time = new Date(date);
    time.setHours(Number(hour), Number(minute), 0, 0);
    return isMomentInClosingPeriod(time, periods, { inclusiveEnd });
}

/** True when every 15-minute slot in the hour falls inside a closing period. */
export function isHourBlockedByClosing(date, hour, closingPeriods) {
    return [0, 15, 30, 45].every((m) => isTimeInClosingPeriod(date, hour, m, closingPeriods));
}

export function formatClosingPeriodTime(date) {
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
}

/** Items for AgencyHours: { start_hour, end_hour, status: 'unavailable', isClosingPeriod: true } */
/** True when both start and end of an availability slot fall inside a closing period. */
export function isAvailabilitySlotFullyClosed(item, date, closingPeriods) {
    if (!item?.start_hour || !item?.end_hour || !date) return false;
    const [sh, sm] = item.start_hour.split(':').map(Number);
    const [eh, em] = item.end_hour.split(':').map(Number);
    return (
        isTimeInClosingPeriod(date, sh, sm, closingPeriods, { inclusiveEnd: true })
        && isTimeInClosingPeriod(date, eh, em, closingPeriods, { inclusiveEnd: true })
    );
}

export function filterAvailabilitiesForClosing(availabilities, date, closingPeriods) {
    if (!availabilities?.length || !closingPeriods?.length || !date) return availabilities ?? [];
    return availabilities.filter((item) => !isAvailabilitySlotFullyClosed(item, date, closingPeriods));
}

export function getClosingPeriodDisplayItems(closingPeriods, date) {
    return getClosingPeriodsForDate(closingPeriods, date).map((p) => {
        const start = parseClosingDate(p.start_date);
        const end = parseClosingDate(p.end_date);
        if (!start || !end) return null;
        return {
            start_hour: formatClosingPeriodTime(start),
            end_hour: formatClosingPeriodTime(end),
            status: 'unavailable',
            isClosingPeriod: true,
        };
    }).filter(Boolean);
}
